import { FC, ReactNode, Suspense, lazy, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Auth from "../services/auth";
import routes from "../config/routes";
import IRoute from "../types/route";
import http from "../services/http";
import { isViewOwnProfile } from "../utils/isViewOwnProfile";
import { canChangeOwnPassword } from "../utils/common";
import { isOrganizationAdministrator, hasAdvisorLikeAccess } from "../utils/permissionUtils";
import { PortalAuthScreenThemeSync, useDarkMode } from "../context/DarkModeContext";
import { DatabaseErrorProvider, useDatabaseError } from "../context/DatabaseErrorContext";
import AuthenticatedGate from "./common/AuthenticatedGate";
import useRouteMeta from "../hooks/useRouteMeta";
import { isAriaUrl, getAriaAppBaseUrl, isStandaloneAriaShell } from "../utils/ariaUrls";
import { Capacitor } from "@capacitor/core";
import { handleNotificationTap } from "../services/pushNotificationService";
import { storeUserWithTokenClaims, loadUserFromStorage } from "../utils/authStorage";
import deepLinkingService from "../services/deepLinkingService";
import RouteUsageTracker from "./RouteUsageTracker";
import RouteFeatureStylesLoader from "./RouteFeatureStylesLoader";
import NavigationHistoryRecorder from "./NavigationHistoryRecorder";
import ImpersonationAccessNotice from "./ImpersonationAccessNotice";
import GoogleDriveConnectNotice from "./GoogleDriveConnectNotice";
import CrmIntegrationConnectNotice from "./CrmIntegrationConnectNotice";
import OrgAssessmentLinkPreferencesSync from "./OrgAssessmentLinkPreferencesSync";
import NativeAuthRouteGuard from "./NativeAuthRouteGuard";
import { resetNavigationHistory } from "../utils/nativeBackNavigation";
import SettingsLazy from "../config/SettingsLazy";
import { isAssessmentPublicHostname } from "../utils/environmentUrls";
import { changeLocale, resolveActiveLocaleFromStorage } from "../i18n";
import { PENDING_POST_LOGIN_PATH_KEY, stashPostLoginPath } from "../utils/postLoginRedirect";
import { confirmPortalFullScreenEntry } from "../utils/portalFullScreenChat";

// Lazy load heavy components
const SidebarLayout = lazy(() => import("../common/layouts.ts/SidebarLayout"));
const PasswordForgetForm = lazy(() => import("./ForgetPasswordForm"));
const ResetForgetPasswordForm = lazy(() => import("./ResetForgetPasswordForm"));
const DatabaseErrorPage = lazy(() => import("./common/DatabaseErrorPage"));
const ResetPasswordModal = lazy(() => import("./ResetPassword"));

const Dashboard = lazy(() => import('./Dashboard/Dashboard'));
const LoginForm = lazy(() => import('./LoginForm'));
// const UserAssessment = lazy(() => import('./ui/UserAssessment'));
const TargetsSurvey = lazy(() => import('./TargetsSurvey'));
const ChangePassword = lazy(() => import('../common/PasswordChange'));
const EntityOverview = lazy(() => import('./GodMode/GodModeOverview'));
const GodModeAdminOverview = lazy(() => import('./GodMode/GodModeAdminOverview'));
const EntityDetails = lazy(() => import('./Entity/EntityDetails'));
const CRMRecord = lazy(() => import('./CRM/pages/CRMRecord'));
const NewUserAssessment = lazy(() => import('./Assessment'));
const WebsiteLeadThankYou = lazy(() => import('./Assessment/components/UserAssessment/WebsiteLeadThankYou'));
const PremiumLanding = lazy(() => import('./PremiumAssessment/PremiumLanding'));
const PremiumSuccess = lazy(() => import('./PremiumAssessment/PremiumSuccess'));
const AutoLoginComponent = lazy(() => import('./AutoLoginComponent'));
const LoginByToken = lazy(() => import('./LoginByToken'));
const LegalDocumentPage = lazy(() => import('./Legal/LegalDocumentPage'));
const OrderFormPage = lazy(() => import('./OrderForm/OrderFormPage'));
const InvoicePayPage = lazy(() => import('./Billing/InvoicePayPage'));
const AriaChatStandalone = lazy(() => import('./Standalone/AriaChatStandalone'));
const AriaSignup = lazy(() => import('./Standalone/AriaSignup'));
const AriaOnboarding = lazy(() => import('./Standalone/AriaOnboarding'));
const AriaEmailVerification = lazy(() => import('./Standalone/AriaEmailVerification'));
const ChatUrlHandler = lazy(() => import('./ChatUrlHandler'));
interface Props {
    user: any;
    setUser: (u: any) => void;
}

/**
 * Wires up push notification tap handling inside the BrowserRouter context
 * so we have access to useNavigate. Active on any native Capacitor build that uses MainApp (e.g. Portal).
 */
const PushNotificationHandler: FC = () => {
    const navigate = useNavigate();
    useEffect(() => {
        const cleanup = handleNotificationTap(navigate);
        return cleanup;
    }, [navigate]);
    return null;
};

/**
 * Once per WebView / JS session. Survives React StrictMode and effect re-runs (e.g. navigate
 * identity) so iOS does not re-apply App.getLaunchUrl() and snap navigation back to the link.
 */
let portalNativeLaunchUrlConsumed = false;

/**
 * Initializes deep link listeners so universal/app links navigate in-app.
 */
const DeepLinkHandler: FC<{ isAuthenticated: boolean }> = ({ isAuthenticated }) => {
    const navigate = useNavigate();
    const isAuthenticatedRef = useRef(isAuthenticated);
    isAuthenticatedRef.current = isAuthenticated;

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;

        const handleIncomingUrl = (url: string) => {
            if (!deepLinkingService.isHandledUrl(url)) {
                return;
            }

            const path = deepLinkingService.parseDeepLinkUrl(url);

            // Persist protected deep links for post-login redirect.
            const requiresAuth = /^\/(folders|people|teams|entities|crm|settings|positions|analytics|god-mode|chat)(\/|$)/.test(path);
            if (!isAuthenticatedRef.current && requiresAuth) {
                localStorage.setItem(PENDING_POST_LOGIN_PATH_KEY, path);
                navigate('/login', { replace: true });
                resetNavigationHistory('/login');
                return;
            }

            navigate(path, { replace: true });
            resetNavigationHistory(path);
        };

        let cancelled = false;
        void (async () => {
            await deepLinkingService.registerHandler(handleIncomingUrl);
            if (portalNativeLaunchUrlConsumed) {
                return;
            }
            const launchUrl = await deepLinkingService.getLaunchUrl();
            if (cancelled) {
                return;
            }
            portalNativeLaunchUrlConsumed = true;
            if (launchUrl) {
                handleIncomingUrl(launchUrl);
            }
        })();

        return () => {
            cancelled = true;
            deepLinkingService.clearHandler();
        };
    }, [navigate]);

    useEffect(() => {
        if (!isAuthenticated) return;

        const pendingPath = localStorage.getItem(PENDING_POST_LOGIN_PATH_KEY);
        if (!pendingPath) return;

        localStorage.removeItem(PENDING_POST_LOGIN_PATH_KEY);
        navigate(pendingPath, { replace: true });
        resetNavigationHistory(pendingPath);
    }, [isAuthenticated, navigate]);

    return null;
};

const MainApp: FC<Props> = ({ user, setUser }) => {
    const IS_GOD_MODE_USER = user ? user.user.isGodModeUser : false; // hardcoded GodMode userId
    const IS_ADVISOR = user ? hasAdvisorLikeAccess(user.user) : false;
    const CAN_VIEW_ANALYTICS = user ? user?.user?.permissions?.viewAnalytics : false;
    const [isDefaultPassword, setIsDefaultPassword] = useState<boolean>(false);
    const platformContextLocalStorage = localStorage.getItem("platformContext");
    const [showResetModal, setShowResetModal] = useState<boolean>(false);
    const [resetEmail, setResetEmail] = useState<string>('');

    const currentPath = window.location.href;

    // Re-apply the saved UI language whenever the authenticated user changes.
    // The module-load bootstrap in index.tsx only runs once (before login), so a
    // fresh login / account switch would otherwise keep the previous or default
    // language. Resolution order (user.uiLanguage → entity.defaultLanguage → en-US)
    // is handled by resolveLocaleFromStorage().
    const storedUiLanguage = user?.user?.uiLanguage ?? user?.user?.entity?.defaultLanguage ?? null;
    useEffect(() => {
        void resolveActiveLocaleFromStorage().then(changeLocale);
    }, [storedUiLanguage]);

    const handleLogin = async (email: string, password: string, token?: string) => {
        try {
            const data = await Auth.loginWithEmail(email, password, token);
            localStorage.removeItem("oldUser");
            sessionStorage.removeItem('aiTermsServiceRetryAttempt');
            sessionStorage.removeItem('serviceInterruptionRetryAttempt');
            if(data.user.isTempByGodModeUser) {
                // Store email in state (not localStorage) and show modal

                setResetEmail(email);
                setShowResetModal(true);
            } else {
                storeUserWithTokenClaims(data);
                localStorage.setItem("platformContext", data.user.platformContext);
                http.setAuthToken(data.token);
                sessionStorage.removeItem('serviceInterruptionRetryAttempt');
                if (Capacitor.isNativePlatform()) {
                    resetNavigationHistory('/');
                    window.location.replace('/');
                } else {
                    const path = window.location.pathname;
                    const destination = path === '/login' || path === '/aria/login' ? '/' : path + window.location.search;
                    window.location.href = destination;
                }
            }
        } catch (err) {
            console.error("Login error:", err);
            throw err;
        }
    };

    const handleAutoLogin = async (email: string, password: string) => {
        try {
            const data = await Auth.loginWithEmail(email, password);
            const sanitizedData = storeUserWithTokenClaims(data);
            setUser(sanitizedData);
            localStorage.setItem("platformContext", data.user.platformContext);
            http.setAuthToken(data.token);
            
            // For auto-login, redirect to root path
            // The main app will handle redirecting to change password if needed
            if (Capacitor.isNativePlatform()) {
                resetNavigationHistory('/');
                window.location.replace('/');
            } else {
                window.location.href = '/';
            }
        } catch (err) {
            console.error("Auto-login error:", err);
            throw err;
        }
    };

    const handleAriaLogin = async (email: string, password: string, token?: string) => {
        try {
            const data = await Auth.loginWithEmail(email, password, token);
            const sanitizedData = storeUserWithTokenClaims(data);
            setUser(sanitizedData);
            sessionStorage.removeItem('aiTermsServiceRetryAttempt');
            localStorage.setItem("platformContext", data.user.platformContext);
            http.setAuthToken(data.token);
            
            // Check if user wants to add Aria membership (from query parameter)
            const urlParams = new URLSearchParams(window.location.search);
            const addAria = urlParams.get('addAria');
            
            if (addAria === 'true') {
                try {
                    // Import ariaOnboardingService dynamically
                    const ariaOnboardingService = await import('../services/ariaOnboardingService');
                    // Add Aria membership to existing account
                    await ariaOnboardingService.default.addAriaMembership();
                } catch (addAriaError) {
                    console.error('Error adding Aria membership:', addAriaError);
                    // Continue anyway - user can add it later
                }
            }
            
            // For Aria login, redirect to Aria root
            if (isAriaRoute) {
                window.location.href = '/';
            } else {
                window.location.href = '/aria/';
            }
        } catch (err) {
            console.error("Aria login error:", err);
            throw err;
        }
    };

    useEffect(() => {
        const localUser = user?.user;
        if (localUser) {
            if (!canChangeOwnPassword()) {
                setIsDefaultPassword(false);
            } else {
                // If user can change their own password, check if they need to set a default password
                setIsDefaultPassword(!localUser.isDefaultPasswordChanged);
            }
        } else {
            setIsDefaultPassword(false);
        }
    }, [user?.user]);

    const isPublicRoute = isAssessmentPublicHostname(window.location.hostname);
    const isAriaRoute = ['app.ariachat.ai', 'beta.ariachat.ai'].includes(window.location.hostname);
    const isAriaAssessmentRoute = ['me.ariachat.ai', 'test.ariachat.ai'].includes(window.location.hostname);
    const isAriaSignupRoute = ['signup.ariachat.ai', 'me.ariachat.ai'].includes(window.location.hostname);

    return (
        <DatabaseErrorProvider>
            <MainAppContent 
                user={user} 
                setUser={setUser}
                isPublicRoute={isPublicRoute}
                isAriaRoute={isAriaRoute}
                isAriaAssessmentRoute={isAriaAssessmentRoute}
                isAriaSignupRoute={isAriaSignupRoute}
                isDefaultPassword={isDefaultPassword}
                platformContextLocalStorage={platformContextLocalStorage}
                showResetModal={showResetModal}
                setShowResetModal={setShowResetModal}
                resetEmail={resetEmail}
                setResetEmail={setResetEmail}
                currentPath={currentPath}
                handleLogin={handleLogin}
                handleAutoLogin={handleAutoLogin}
                handleAriaLogin={handleAriaLogin}
                IS_GOD_MODE_USER={IS_GOD_MODE_USER}
                IS_ADVISOR={IS_ADVISOR}
                CAN_VIEW_ANALYTICS={CAN_VIEW_ANALYTICS}
            />
        </DatabaseErrorProvider>
    );
};

// Separate component to use the database error hook
const MainAppContent: FC<{
    user: any;
    setUser: (u: any) => void;
    isPublicRoute: boolean;
    isAriaRoute: boolean;
    isAriaAssessmentRoute: boolean;
    isAriaSignupRoute: boolean;
    isDefaultPassword: boolean;
    platformContextLocalStorage: string | null;
    showResetModal: boolean;
    setShowResetModal: (show: boolean) => void;
    resetEmail: string;
    setResetEmail: (email: string) => void;
    currentPath: string;
    handleLogin: (email: string, password: string, token?: string) => Promise<void>;
    handleAutoLogin: (email: string, password: string) => Promise<void>;
    handleAriaLogin: (email: string, password: string, token?: string) => Promise<void>;
    IS_GOD_MODE_USER: boolean;
    IS_ADVISOR: boolean;
    CAN_VIEW_ANALYTICS: boolean;
}> = ({
    user,
    isPublicRoute,
    isAriaRoute,
    isAriaAssessmentRoute,
    isAriaSignupRoute,
    isDefaultPassword,
    platformContextLocalStorage,
    showResetModal,
    setShowResetModal,
    resetEmail,
    handleLogin,
    handleAutoLogin,
    handleAriaLogin,
    IS_GOD_MODE_USER,
    IS_ADVISOR,
    CAN_VIEW_ANALYTICS,
}) => {
    const { hasDatabaseError } = useDatabaseError();
    const { darkMode, setDarkMode } = useDarkMode();

    // Show database error page if there's a database connection issue
    if (hasDatabaseError) {
        return (
            <Suspense>
                <DatabaseErrorPage />
            </Suspense>
        );
    }

    // Check if Aria-only user is trying to access Aptive portal
    // Get user from prop or localStorage (handles case where user was just logged in)
    const getUserFromStorage = () => {
        return loadUserFromStorage();
    };

    const currentUser = user || getUserFromStorage();
    const HAS_CRM_ACCESS = currentUser?.user?.permissions?.crm === true;
    const userPlatformContext = currentUser?.user?.platformContext;
    const isAriaOnlyUser = userPlatformContext === 'aria_chat';
    
    // Check if we're on an Aria URL (using existing utility that handles localhost)
    const isOnAriaUrl = isAriaUrl();
    
    // Check if we're on an Aptive URL (not Aria)
    const isOnAptiveUrl = !isOnAriaUrl && !isPublicRoute;
    
    // Block Aria-only users from accessing Aptive portal
    if (isAriaOnlyUser && isOnAptiveUrl && currentUser) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: 'var(--background)',
                color: 'var(--ink)'
            }}>
                <h1 style={{ 
                    fontSize: '2rem', 
                    marginBottom: '1rem',
                    color: 'var(--error-color)'
                }}>
                    Access Denied
                </h1>
                <p style={{ 
                    fontSize: '1.1rem', 
                    marginBottom: '2rem',
                    maxWidth: '500px',
                    lineHeight: '1.6'
                }}>
                    Your account is only authorized to access Ask Aria. Please visit{' '}
                    <a 
                        href={getAriaAppBaseUrl()} 
                        style={{ color: 'var(--primary)', textDecoration: 'underline' }}
                    >
                        {getAriaAppBaseUrl().replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                    {' '}to access your account.
                </p>
                <button
                    onClick={() => {
                        window.location.href = getAriaAppBaseUrl();
                    }}
                    style={{
                        padding: '0.75rem 1.5rem',
                        fontSize: '1rem',
                        backgroundColor: 'var(--primary)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Go to Ask Aria
                </button>
            </div>
        );
    }

    // Component to handle route-based meta tags (must be inside BrowserRouter)
    const RouteMetaHandler = () => {
        // Automatically set og:image based on route (disabled for Aria routes which set their own meta)
        useRouteMeta(null, !isAriaRoute && !isAriaAssessmentRoute && !isAriaSignupRoute);
        return null;
    };

    return (
        <>
            <BrowserRouter>
            <PortalAuthScreenThemeSync isLoggedIn={Boolean(currentUser?.token)} />
            <NavigationHistoryRecorder />
            <NativeAuthRouteGuard isAuthenticated={!!user} />
            <PushNotificationHandler />
            <DeepLinkHandler isAuthenticated={!!user} />
            <RouteMetaHandler />
            <RouteFeatureStylesLoader />
            <RouteUsageTracker />
            <ImpersonationAccessNotice />
            <GoogleDriveConnectNotice />
            <CrmIntegrationConnectNotice />
            <OrgAssessmentLinkPreferencesSync
                entityId={user?.user?.currentEntityId || user?.user?.entityId}
            />
            <Routes>
                {/* Public click-to-accept pages: no login, same on every hostname, and open even before a
                    default password is changed (they outrank the change-password catch-all). */}
                <Route path="/legal/:slug" element={<Suspense><LegalDocumentPage /></Suspense>} />
                <Route path="/legal/:slug/versions" element={<Suspense><LegalDocumentPage showVersions /></Suspense>} />
                <Route path="/legal/:slug/v/:version" element={<Suspense><LegalDocumentPage /></Suspense>} />
                <Route path="/order/:token" element={<Suspense><OrderFormPage /></Suspense>} />
                <Route path="/pay/:token" element={<Suspense><InvoicePayPage /></Suspense>} />
                {isDefaultPassword ?
                    <Route
                        path="*"
                        element={
                            <AuthenticatedGate>
                                <Suspense>
                                    <SidebarLayout
                                        darkMode={darkMode}
                                        setDarkMode={setDarkMode}
                                    >
                                        <Suspense>
                                            <ChangePassword />
                                        </Suspense>
                                    </SidebarLayout>
                                </Suspense>
                            </AuthenticatedGate>
                        }
                    />
                    :
                    <>
                        <Route
                            path="/login"
                            element={!user ? (
                                <Suspense><LoginForm onLogin={isAriaRoute ? handleAriaLogin : handleLogin} /></Suspense>
                            ) : (
                                <Navigate replace to="/" />
                            )} />
                        <Route
                            path="/aria/login"
                            element={!user ? (
                                <Suspense><LoginForm onLogin={handleAriaLogin} /></Suspense>
                            ) : (
                                <Navigate replace to="/aria/" />
                            )} />
                        <Route
                            path="/login-by-token"
                            element={
                                <Suspense>
                                    <LoginByToken />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/password-forget"
                            element={!user ? (
                                <Suspense>
                                    <PasswordForgetForm />
                                </Suspense>
                            ) : (
                                <Navigate replace to="/" />
                            )} />
                        <Route
                            path="/reset-forget-password/:token?"
                            element={!user ? (
                                <Suspense>
                                    <ResetForgetPasswordForm onAutoLogin={handleAutoLogin} />
                                </Suspense>
                            ) : (
                                <Navigate replace to="/" />
                            )} />

                        <Route
                            path="/assessment/:token?"
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            }
                        />
                        {/* Short URL Routes for Assessment */}
                        <Route
                            path="/assessment/s/:token"
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/assessment/i/:token"
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            }
                        />
                        {/* Direct Short URL Routes (without /assessment/ prefix) */}
                        <Route
                            path="/s/:token"
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/i/:token"
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            }
                        />
                        {/* Aria Standalone App Routes */}
                        {/* Invitation token route must come before the generic assessment route */}
                        <Route
                            path="/aria/assessment/i/:token"
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} isAriaAssessment={true}/>
                                </Suspense>
                            }
                        />
                        <Route
                            path="/aria/assessment/:token?"
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} isAriaAssessment={true}/>
                                </Suspense>
                            }
                        />
                        {/* Signup route - render directly without AriaChatStandalone to avoid auth hooks */}
                        <Route
                            path="/signup"
                            element={
                                <Suspense>
                                    <AriaSignup darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            }
                        />
                        {/* Onboarding flow - handles all onboarding steps */}
                        {/* Routes without /aria prefix for staging/production */}
                        {isAriaRoute && (
                            <>
                                <Route
                                    path="/create-account"
                                    element={
                                        <Suspense>
                                            <AriaOnboarding darkMode={darkMode} setDarkMode={setDarkMode} />
                                        </Suspense>
                                    }
                                />
                                <Route
                                    path="/verify-email"
                                    element={
                                        <Suspense>
                                            <AriaEmailVerification darkMode={darkMode} setDarkMode={setDarkMode} />
                                        </Suspense>
                                    }
                                />
                                <Route
                                    path="/interests"
                                    element={
                                        <InterestsRouteGuard 
                                            user={user} 
                                            isAriaRoute={isAriaRoute}
                                            darkMode={darkMode}
                                            setDarkMode={setDarkMode}
                                        />
                                    }
                                />
                            </>
                        )}
                        {/* Routes with /aria prefix for localhost */}
                        <Route
                            path="/aria/create-account"
                            element={
                                <Suspense>
                                    <AriaOnboarding darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/aria/verify-email"
                            element={
                                <Suspense>
                                    <AriaEmailVerification darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/aria/interests"
                            element={
                                <InterestsRouteGuard 
                                    user={user} 
                                    isAriaRoute={false}
                                    darkMode={darkMode}
                                    setDarkMode={setDarkMode}
                                />
                            }
                        />
                        <Route
                            path="/aria/*"
                            element={
                                <Suspense>
                                    <AriaChatStandalone darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            }
                        />
                        
                        {/* Portal full-screen Ask Aria — same shell as app.ariachat.ai, keeps portal JWT */}
                        <Route
                            path="/chat/*"
                            element={
                                isAriaRoute ? (
                                    <Navigate replace to="/" />
                                ) : (
                                    <PortalFullScreenChatRoute
                                        user={user}
                                        darkMode={darkMode}
                                        setDarkMode={setDarkMode}
                                    />
                                )
                            }
                        />

                        {/* Chat URL Routes - for direct access to chat sessions */}
                        {/* /m/:urlToken - Dashboard with chat opened (for aptive_index users) */}
                        <Route
            path="/m/:urlToken"
            element={
                !user ? (
                    <Navigate replace to="/login" />
                ) : (
                    <AuthenticatedGate>
                        <Suspense>
                            <ChatUrlHandler />
                        </Suspense>
                    </AuthenticatedGate>
                )
            }
        />
        <Route
            path="/share/:urlToken"
            element={
                <Suspense>
                    <AriaChatStandalone darkMode={darkMode} setDarkMode={setDarkMode} />
                </Suspense>
            }
        />
                        <Route
                            path="/"
                            element={
                                isPublicRoute || isAriaAssessmentRoute ? (
                                    <Suspense><NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} isAriaAssessment={isAriaAssessmentRoute} /></Suspense>
                                ) : isAriaSignupRoute ? (
                                    <Suspense><AriaSignup darkMode={darkMode} setDarkMode={setDarkMode} /></Suspense>
                                ) : isAriaRoute ? (
                                    !user ? (
                                        <Navigate replace to="/create-account" />
                                    ) : (
                                        <AuthenticatedGate>
                                            <Suspense><AriaChatStandalone darkMode={darkMode} setDarkMode={setDarkMode} /></Suspense>
                                        </AuthenticatedGate>
                                    )
                                ) : !user ? (
                                    <Navigate replace to="/login" />
                                ) : (
                                    <AuthenticatedGate>
                                        <Suspense>
                                            <SidebarLayout darkMode={darkMode} setDarkMode={setDarkMode}>
                                                <Suspense>
                                                    <Dashboard />
                                                </Suspense>
                                            </SidebarLayout>
                                        </Suspense>
                                    </AuthenticatedGate>
                                )
                            } />

                        {/* Premium Assessment Routes */}
                        <Route
                            path="/premium"
                            element={
                                <Suspense>
                                    <PremiumLanding darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/premium/success"
                            element={
                                <Suspense>
                                    <PremiumSuccess darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            }
                        />

                        {/* Auto Login Route */}
                        <Route
                            path="/auto-login"
                            element={
                                <Suspense>
                                    <AutoLoginComponent onLogin={handleAutoLogin} />
                                </Suspense>
                            }
                        />

                        {/* Lead Capture Thank You Page */}
                        <Route 
                            path="/assessment/thank-you-calendar-page" 
                            element={
                                <Suspense>
                                    <WebsiteLeadThankYou darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            } 
                        />
                        
                        {/* Persistent Thank You Page */}
                        <Route 
                            path="/assessment/thank-you/:sessionId" 
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            } 
                        />

                        <Route 
                            path="/assessment/aria/thank-you/:sessionId" 
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} isAriaAssessment={true} />
                                </Suspense>
                            } 
                        />
                        
                        {/* Bookmarkable results page, keyed by the per-result secret token */}
                        <Route 
                            path="/results/:resultsToken" 
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            } 
                        />

                        <Route 
                            path="/assessment/aria/results/:resultsToken" 
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} isAriaAssessment={true} />
                                </Suspense>
                            } 
                        />

                        {/* Legacy thank-you URLs (UserScores id): served for 24h after completion, then redirect to /results/:token */}
                        <Route 
                            path="/thank-you/:sessionId" 
                            element={
                                <Suspense>
                                    <NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} />
                                </Suspense>
                            } 
                        />

                        <Route path="/positions/survey/:targetId" element={<Suspense><TargetsSurvey /></Suspense>} />

                        {
                            (IS_GOD_MODE_USER || IS_ADVISOR || HAS_CRM_ACCESS) &&
                            <>
                                <Route path="/entities/" element={
                                    <AuthenticatedGate>
                                        <Suspense>
                                            <SidebarLayout darkMode={darkMode} setDarkMode={setDarkMode}>
                                                <Suspense><EntityOverview /></Suspense>
                                            </SidebarLayout>
                                        </Suspense>
                                    </AuthenticatedGate>
                                } />
                                <Route path="/entities/:entityId?" element={
                                    <AuthenticatedGate>
                                        <Suspense>
                                            <SidebarLayout darkMode={darkMode} setDarkMode={setDarkMode}>
                                                <Suspense><EntityDetails /></Suspense>
                                            </SidebarLayout>
                                        </Suspense>
                                    </AuthenticatedGate>
                                } />
                            </>
                        }

                        {IS_GOD_MODE_USER && (
                            <Route path="/god-mode" element={
                                <AuthenticatedGate>
                                    <Suspense>
                                        <SidebarLayout darkMode={darkMode} setDarkMode={setDarkMode}>
                                            <Suspense><GodModeAdminOverview /></Suspense>
                                        </SidebarLayout>
                                    </Suspense>
                                </AuthenticatedGate>
                            } />
                        )}

                        {(IS_GOD_MODE_USER || IS_ADVISOR || HAS_CRM_ACCESS) && (
                            <>
                                <Route path="/crm" element={<Navigate to="/entities?tab=CRM" replace />} />
                                <Route path="/crm/pipeline" element={<Navigate to="/entities?tab=CRM" replace />} />
                                <Route path="/crm/clients" element={<Navigate to="/entities?tab=CRM" replace />} />
                                {/* CRM integrations are personal now (profile menu → Settings → Integrations) */}
                                <Route path="/crm/settings" element={<Navigate to="/entities?tab=CRM" replace />} />
                                <Route
                                    path="/crm/record/:type/:id"
                                    element={
                                        <AuthenticatedGate>
                                            <Suspense>
                                                <SidebarLayout darkMode={darkMode} setDarkMode={setDarkMode}>
                                                    <Suspense>
                                                        <CRMRecord />
                                                    </Suspense>
                                                </SidebarLayout>
                                            </Suspense>
                                        </AuthenticatedGate>
                                    }
                                />
                            </>
                        )}

                        {user ? (
                            routes.map((route: IRoute, index) => {
                                const { layout: Layout } = route;
                                return (
                                    <Route
                                        element={
                                            <AuthenticatedGate>
                                                <Suspense>
                                                    <Layout
                                                        darkMode={darkMode}
                                                        setDarkMode={setDarkMode}
                                                    />
                                                </Suspense>
                                            </AuthenticatedGate>
                                        }
                                        path={route.path}
                                        key={`${route.layout}${index}`}
                                    >
                                        <Route path="/" element={<Suspense><Dashboard /></Suspense>} />
                                        {route.routes.map((newRoute) => {
                                            const { name, path, component: Component } = newRoute;
                                            return (
                                                <Route
                                                    key={`${newRoute.path}${index}`}
                                                    path={path}
                                                    element={
                                                        <IsOwnProfileLayout path={newRoute.path}>
                                                            <Component name={name} {...route.props} />
                                                        </IsOwnProfileLayout>
                                                    }
                                                />
                                            );
                                        })}
                                    </Route>
                                );
                            })
                        ) : (
                            <>
                                <Route path="/assessment" element={<Suspense><NewUserAssessment darkMode={darkMode} setDarkMode={setDarkMode} /></Suspense>} />
                                <Route path="/aria" element={<Navigate replace to="/aria/create-account" />} />
                                <Route path="*" element={<Navigate replace to="/login" />} />
                            </>
                        )}

                        {/* Settings — org admins / god mode. Advisors' integrations are personal (User Settings → Integrations). */}
                        <Route
                            path="/settings"
                            element={
                                !user ? (
                                    <Navigate replace to="/login" />
                                ) : user?.user?.isGodModeUser ||
                                  isOrganizationAdministrator(user?.user?.role) ? (
                                    <AuthenticatedGate>
                                        <Suspense>
                                            <SidebarLayout darkMode={darkMode} setDarkMode={setDarkMode}>
                                                <SettingsLazy />
                                            </SidebarLayout>
                                        </Suspense>
                                    </AuthenticatedGate>
                                ) : (
                                    <Navigate replace to="/" />
                                )
                            }
                        />
                        
                        {/* Root path with token - only for assessment, not Aria (must be last) */}
                        <Route 
                            path="/:token" 
                            element={
                                isAriaRoute ? (
                                    <Navigate replace to="/" />
                                ) : (
                                    <Suspense>
                                        <AssessmentTokenRoute
                                            user={user}
                                            isPublicRoute={isPublicRoute}
                                            isAriaAssessmentRoute={isAriaAssessmentRoute}
                                            darkMode={darkMode}
                                            setDarkMode={setDarkMode}
                                        />
                                    </Suspense>
                                )
                            } 
                        />
                    </>}
            </Routes>
        </BrowserRouter>
        
        {/* Reset Password Modal - shown when user needs to reset password after GodMode reset */}
        <Suspense>
            <ResetPasswordModal 
                isOpen={showResetModal}
                onClose={() => setShowResetModal(false)}
                email={resetEmail}
                handleAutoLogin={handleAutoLogin}
            />
        </Suspense>
        </>
    );
};

export default MainApp;



interface LayoutProps {
    children: ReactNode;
    path?: string;
}

/**
 * The /:token catch-all is only for dedicated assessment hostnames (assessment.aptiveindex.com,
 * me.ariachat.ai, etc.). On the portal host it must not run — expired sessions leave paths like
 * /people unmatched by auth routes and would otherwise hit this catch-all as a fake "token".
 */
const AssessmentTokenRoute: FC<{
    user: any;
    isPublicRoute: boolean;
    isAriaAssessmentRoute: boolean;
    darkMode: 'light' | 'dark' | 'system';
    setDarkMode: (mode: 'light' | 'dark' | 'system') => void;
}> = ({ user, isPublicRoute, isAriaAssessmentRoute, darkMode, setDarkMode }) => {
    if (!isPublicRoute && !isAriaAssessmentRoute) {
        return <Navigate replace to={user ? '/' : '/login'} />;
    }

    return (
        <NewUserAssessment
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            isAriaAssessment={isAriaAssessmentRoute}
        />
    );
};

const PortalFullScreenChatRoute: FC<{
    user: any;
    darkMode: 'light' | 'dark' | 'system';
    setDarkMode: (mode: 'light' | 'dark' | 'system') => void;
}> = ({ user, darkMode, setDarkMode }) => {
    useEffect(() => {
        confirmPortalFullScreenEntry();
    }, []);

    const userFromStorage = loadUserFromStorage();
    const isAuthenticated = user || userFromStorage;

    if (!isAuthenticated) {
        stashPostLoginPath('/chat');
        return <Navigate replace to="/login" />;
    }

    return (
        <Suspense fallback={null}>
            <AriaChatStandalone darkMode={darkMode} setDarkMode={setDarkMode} />
        </Suspense>
    );
};

// Component to guard interests route - checks both user prop and localStorage
// This handles the case where user was just created and stored in localStorage,
// but the user prop hasn't been updated yet due to page reload
const InterestsRouteGuard: FC<{
    user: any;
    isAriaRoute: boolean;
    darkMode: 'light' | 'dark' | 'system';
    setDarkMode: (mode: 'light' | 'dark' | 'system') => void;
}> = ({ user, isAriaRoute, darkMode, setDarkMode }) => {
    // Check both user prop and localStorage as fallback
    // This handles the case where user was just created and stored in localStorage,
    // but the user prop hasn't been updated yet due to page reload from window.location.href
    const getUserFromStorage = () => {
        return loadUserFromStorage();
    };

    const userFromStorage = getUserFromStorage();
    const isAuthenticated = user || userFromStorage;

    if (!isAuthenticated) {
        return <Navigate replace to={isAriaRoute ? "/login" : "/aria/login"} />;
    }

    return (
        <Suspense>
            <AriaOnboarding darkMode={darkMode} setDarkMode={setDarkMode} />
        </Suspense>
    );
};

export const IsOwnProfileLayout: FC<LayoutProps> = ({
    children,
    path = ""
}) => {
    // Don't redirect if we're in Aria context (mobile app OR standalone Aria on web)
    // This prevents redirects when Aria context accidentally hits main app routes
    // Mobile app uses separate router, and standalone Aria should stay in Aria routes
    const isMobileApp = typeof window !== 'undefined' && Capacitor.isNativePlatform();
    const isAriaContext = isMobileApp || (typeof window !== 'undefined' && isStandaloneAriaShell());
    
    if (isAriaContext) {
        return <>{children}</>;
    }

    const userDetail = JSON.parse(localStorage.getItem('user') || "{}");
    const isDefaultPassword = !userDetail.user.isDefaultPasswordChanged;

    const ignoredPaths = ['/people/:id', '/teams', '/teams/:groupId', ...(isDefaultPassword ? ['/set-password'] : [])];

    


    if (ignoredPaths.includes(path)) {
        return <>{children}</>;
    }

    if (isViewOwnProfile()) {
        return <Navigate to={`/people/${userDetail.user.userId}`} replace />
    }

    return <>{children}</>;
};


